import { ref } from 'vue';

const FUNCTIONS_BASE_URL = 'https://us-central1-trendztube.cloudfunctions.net/manageSubscription';

/**
 * Get subscription status
 * @param {Object} userPlan - User plan object from profile
 * @returns {Object}
 */
export function getSubscriptionStatus(userPlan) {
  if (!userPlan) {
    return {
      isActive: false,
      planName: 'free',
      canUpgrade: true,
      canDowngrade: false,
      canCancel: false
    };
  }

  const planHierarchy = { free: 0, starter: 1, pro: 2 };
  const currentLevel = planHierarchy[userPlan.name] || 0;

  return {
    isActive: userPlan.active,
    planName: userPlan.name,
    canUpgrade: currentLevel < 2, // Can upgrade if not on Pro
    canDowngrade: currentLevel > 1, // Can downgrade from Pro to Starter
    canCancel: currentLevel > 0 // Can cancel if on paid plan
  };
}

/**
 * Subscription composable using Firebase Functions
 */
export function useSubscription() {
  const loading = ref(false);
  const error = ref(null);

  /**
   * Cancel a subscription via Firebase Function
   * Note: The actual user document update will happen via webhook
   */
  const cancelSubscription = async (subscriptionId, userId) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(FUNCTIONS_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'cancel',
          subscriptionId,
          userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel subscription');
      }

      // Don't update Firestore here - the webhook will handle it
      return data.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Resume a subscription via Firebase Function
   * Note: The actual user document update will happen via webhook
   */
  const resumeSubscription = async (subscriptionId, userId) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(FUNCTIONS_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'resume',
          subscriptionId,
          userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resume subscription');
      }

      // Don't update Firestore here - the webhook will handle it
      return data.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Get subscription details via Firebase Function
   */
  const getSubscription = async (subscriptionId, userId) => {
    loading.value = true;
    error.value = null;

    try {
      const response = await fetch(FUNCTIONS_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'get',
          subscriptionId,
          userId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch subscription');
      }

      return data.data;
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  return {
    loading,
    error,
    cancelSubscription,
    resumeSubscription,
    getSubscription
  };
}
